import type {
  ExternalCourse,
  ExternalSubject,
  SubjectRequest,
  SubjectRequestEditPayload,
  WorkExperience,
} from './api'
import { courseSubjects } from './display'
import { subjectTriggersLegacyUnspecifiedWarning } from './precedents'

export type PendingSubjectRequestTarget = {
  external_course_id?: number
  external_subject_id?: number
  work_experience_id?: number
  subject_code: string | null
}

export const externalCourseRequestRemovalMessage = 'Are you sure you want to remove this subject request?'
export const externalSubjectRequestRemovalMessage = 'Are you sure you want to remove this subject?'
export const workExperienceSubjectRequestRemovalMessage = 'Are you sure you want to remove this subject request?'

export function externalCourseRequestTarget(externalCourse: Pick<ExternalCourse, 'external_course_id'>): PendingSubjectRequestTarget {
  return {
    external_course_id: externalCourse.external_course_id,
    subject_code: null,
  }
}

export function externalSubjectRequestTarget(externalSubject: Pick<ExternalSubject, 'external_subject_id'>): PendingSubjectRequestTarget {
  return {
    external_subject_id: externalSubject.external_subject_id,
    subject_code: null,
  }
}

export function workExperienceSubjectRequestTarget(workExperience: Pick<WorkExperience, 'work_experience_id'>): PendingSubjectRequestTarget {
  return {
    work_experience_id: workExperience.work_experience_id,
    subject_code: null,
  }
}

export function setSubjectRequestTargetCode(
  target: PendingSubjectRequestTarget | null,
  subjectCode: string | null,
): PendingSubjectRequestTarget | null {
  if (target == null) return null

  return {
    ...target,
    subject_code: subjectCode,
  }
}

export function buildSubjectRequestPayload(
  target: PendingSubjectRequestTarget | null,
  subjectCode: string,
): SubjectRequestEditPayload | null {
  if (target == null) return null

  if (target.external_course_id != null) {
    return {
      external_course_id: target.external_course_id,
      subject_code: subjectCode,
    }
  }

  if (target.external_subject_id != null) {
    return {
      external_subject_id: target.external_subject_id,
      subject_code: subjectCode,
    }
  }

  if (target.work_experience_id != null) {
    return {
      work_experience_id: target.work_experience_id,
      subject_code: subjectCode,
    }
  }

  return null
}

export function externalCourseRequestRemovalPayload(
  course: Pick<ExternalCourse, 'external_course_id'>,
  request: Pick<SubjectRequest, 'subject_code'>,
): SubjectRequestEditPayload {
  return {
    external_course_id: course.external_course_id,
    subject_code: request.subject_code,
  }
}

export function externalSubjectRequestRemovalPayload(
  subject: Pick<ExternalSubject, 'external_subject_id'>,
  request: Pick<SubjectRequest, 'subject_code'>,
): SubjectRequestEditPayload {
  return {
    external_subject_id: subject.external_subject_id,
    subject_code: request.subject_code,
  }
}

export function workExperienceSubjectRequestRemovalPayload(
  workExperience: Pick<WorkExperience, 'work_experience_id'>,
  request: Pick<SubjectRequest, 'subject_code'>,
): SubjectRequestEditPayload {
  return {
    work_experience_id: workExperience.work_experience_id,
    subject_code: request.subject_code,
  }
}

export function findExternalSubjectForRequest(
  target: PendingSubjectRequestTarget | null,
  externalCourses: ExternalCourse[],
): ExternalSubject | null {
  const externalSubjectId = target?.external_subject_id
  if (externalSubjectId == null) return null

  for (const course of externalCourses) {
    const subject = courseSubjects(course).find((item) => item.external_subject_id === externalSubjectId)
    if (subject) return subject
  }

  return null
}

export function hasLegacyUnspecifiedCredit(externalCourses: ExternalCourse[]): boolean {
  return externalCourses.some((course) => {
    return courseSubjects(course).some((subject) => {
      return subjectTriggersLegacyUnspecifiedWarning(subject)
    })
  })
}

export function shouldShowUnspecifiedCreditWarning(warningEnabled: boolean, unspecifiedCreditDetected: boolean): boolean {
  return warningEnabled && unspecifiedCreditDetected
}
